import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Chip,
    Divider,
    Pagination,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import api from "../api";
import { stripHtml } from "../utils/stringUtils";

// Reusable Responses Modal Component
const ResponsesModal = ({
    open,
    onClose,
    surveyId = null,
    collectorId = null,
    title = "All Responses",
    loadingMessage = "Loading responses..."
}) => {
    const [responses, setResponses] = useState({ data: null, loading: false });
    const [responseDetail, setResponseDetail] = useState({ data: null, loading: false });
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 6,
        count: 0
    });

    const fetchResponses = async (page = 1, pageSize = 12) => {
        setResponses({ data: null, loading: true });
        try {
            const response = await api.getCollectorRespondents(surveyId, collectorId, page, pageSize, '-started_at');
            setResponses({ data: response, loading: false });

            // Update pagination info if response includes count
            if (response && response.count !== undefined) {
                setPagination(prev => ({
                    ...prev,
                    page,
                    pageSize,
                    count: response.count
                }));
            }
        } catch (error) {
            console.error("Failed to fetch responses:", error);
            setResponses({ data: null, loading: false });
        }
    };

    const fetchResponseDetail = async (respondentId) => {
        setResponseDetail({ data: null, loading: true });
        try {
            const response = await api.getCollectorRespondentDetail(respondentId);
            setResponseDetail({ data: response, loading: false });
        } catch (error) {
            console.error("Failed to fetch response details:", error);
            setResponseDetail({ data: null, loading: false });
        }
    };

    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
        fetchResponses(newPage, pagination.pageSize);
    };

    useEffect(() => {
        if (open) {
            setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when modal opens or filters change
            fetchResponses(1, pagination.pageSize);
        }
    }, [open, surveyId, collectorId]);

    return (
        <>
            {/* Main Responses Modal */}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        maxHeight: '90vh',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        background: 'linear-gradient(135deg, #00438E 0%, #4A87C8 100%)',
                        color: 'white',
                        px: 4,
                        py: 3,
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #FFD700, #FFA000)',
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {title}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {responses.data?.count || 0} total responses
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            size="large"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                                borderRadius: '50%'
                            }}
                        >
                            <Close sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {responses.loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 12,
                            flexDirection: 'column',
                            gap: 3
                        }}>
                            <CircularProgress sx={{ color: "#00438E" }} size={40} />
                            <Typography variant="h6" sx={{ color: "textSecondary", fontWeight: 500 }}>
                                {loadingMessage}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[0, 1, 2].map((i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            width: 60,
                                            height: 8,
                                            backgroundColor: 'rgba(0, 67, 142, 0.1)',
                                            borderRadius: 4,
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            animationDelay: `${i * 0.2}s`,
                                            '@keyframes pulse': {
                                                '0%': { opacity: 1 },
                                                '50%': { opacity: 0.5 },
                                                '100%': { opacity: 1 },
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : responses.data?.results?.length > 0 ? (
                        <Box sx={{ p: 4 }}>
                            <Grid container spacing={3}>
                                {responses.data.results.map((respondent) => (
                                    <Grid item xs={12} sm={6} lg={4} key={respondent.id}>
                                        <Card
                                            sx={{
                                                borderRadius: "16px",
                                                boxShadow: "0 4px 20px rgba(0, 67, 142, 0.08)",
                                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                                cursor: "pointer",
                                                border: "1px solid rgba(0, 67, 142, 0.1)",
                                                background: `linear-gradient(135deg, #ffffff 0%, ${respondent.completed ? '#e8f5e8' : '#fff3e0'} 100%)`,
                                                position: 'relative',
                                                overflow: 'hidden',
                                                "&:hover": {
                                                    transform: "translateY(-8px) scale(1.02)",
                                                    boxShadow: "0 12px 32px rgba(0, 67, 142, 0.15)",
                                                    borderColor: "#00438E",
                                                    '&::before': {
                                                        opacity: 0.1
                                                    }
                                                },
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: '4px',
                                                    background: respondent.completed
                                                        ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                                                        : 'linear-gradient(90deg, #ff9800, #ffb74d)',
                                                    transition: 'opacity 0.3s ease'
                                                }
                                            }}
                                            onClick={() => fetchResponseDetail(respondent.id)}
                                        >
                                            <CardContent sx={{ px: 3, pb: 0, pt: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    {/* Main Content */}
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', alignSelf: 'center' }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#00438E',
                                                                textAlign: 'center',
                                                                mb: 1,
                                                                lineHeight: 1.3,
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            <Box component="span" sx={{ backgroundColor: '#4A87C8', color: '#ffffff', fontWeight: 700, padding: '2px 4px', borderRadius: '4px' }}>
                                                                {respondent.answer_ids?.length || 0}
                                                            </Box>
                                                            {' '} answers submitted
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(0, 67, 142, 0.02)', py: 1, px: 2, borderRadius: '4px' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                            <Box component="span" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', backgroundColor: 'rgba(0, 67, 142, 0.05)', color: '#00438E', fontWeight: 700, padding: '2px 4px', borderRadius: '4px', fontSize: '0.8rem' }}>
                                                                Survey:
                                                            </Box>
                                                            <Typography variant="caption" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: 500, color: 'text.secondary', fontSize: '0.7rem' }}>
                                                                {respondent.survey_name || 'Unknown Assessment'}
                                                            </Typography>
                                                        </Box>

                                                        <Divider orientation="vertical" flexItem />

                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                            <Box component="span" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', backgroundColor: 'rgba(0, 67, 142, 0.05)', color: '#00438E', fontWeight: 700, padding: '2px 4px', borderRadius: '4px', fontSize: '0.8rem' }}>
                                                                Collector:
                                                            </Box>
                                                            <Typography variant="caption" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: 500, color: 'text.secondary', fontSize: '0.7rem' }}>
                                                                {respondent.collector_name || 'Unknown Collector'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Timeline with Hours */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 0, width: '100%' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.7rem' }}>
                                                        <FormattedMessage id="home.started" defaultMessage="Started" />
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#00438E', fontSize: '0.7rem' }}>
                                                        {new Date(respondent.started_at).toLocaleString('en-US', {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: false
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Pagination */}
                            {pagination.count > pagination.pageSize && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 4,
                                    pb: 2
                                }}>
                                    <Pagination
                                        count={Math.ceil(pagination.count / pagination.pageSize)}
                                        page={pagination.page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                            },
                                            '& .MuiPaginationItem-root.Mui-selected': {
                                                backgroundColor: '#00438E',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#003A75',
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{
                            textAlign: "center",
                            py: 12,
                            px: 4,
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
                        }}>
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(0, 67, 142, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}>
                                <Typography variant="h2" color="#00438E">📊</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                                <FormattedMessage id="home.no_responses" defaultMessage="No responses found." />
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Responses will appear here once users start completing your assessments.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    p: 3,
                    borderTop: '1px solid rgba(0, 67, 142, 0.1)',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
                }}>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        size="large"
                        sx={{
                            background: 'linear-gradient(135deg, #00438E, #4A87C8)',
                            color: 'white',
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 16px rgba(0, 67, 142, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #003A75, #3A76B8)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(0, 67, 142, 0.4)'
                            }
                        }}
                    >
                        <FormattedMessage id="home.close" defaultMessage="Close" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Response Detail Modal */}
            <Dialog
                open={!!responseDetail.data}
                onClose={() => setResponseDetail({ data: null, loading: false })}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        maxHeight: '90vh',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        background: 'linear-gradient(135deg, #00438E 0%, #4A87C8 100%)',
                        color: 'white',
                        px: 4,
                        py: 3,
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #FFD700, #FFA000)',
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                <FormattedMessage id="home.response_details" defaultMessage="Response Details" />
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {responseDetail.data?.answers?.length || 0} questions answered
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => setResponseDetail({ data: null, loading: false })}
                            size="large"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                                borderRadius: '50%'
                            }}
                        >
                            <Close sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {responseDetail.loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 12,
                            flexDirection: 'column',
                            gap: 3
                        }}>
                            <CircularProgress sx={{ color: "#00438E" }} size={40} />
                            <Typography variant="h6" sx={{ color: "textSecondary", fontWeight: 500 }}>
                                <FormattedMessage id="home.loading_response_details" defaultMessage="Loading response details..." />
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[0, 1, 2].map((i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            width: 60,
                                            height: 8,
                                            backgroundColor: 'rgba(0, 67, 142, 0.1)',
                                            borderRadius: 4,
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            animationDelay: `${i * 0.2}s`,
                                            '@keyframes pulse': {
                                                '0%': { opacity: 1 },
                                                '50%': { opacity: 0.5 },
                                                '100%': { opacity: 1 },
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : responseDetail.data?.answers?.length > 0 ? (
                        <Box sx={{ p: 2 }}>
                            {/* Response Header */}
                            <Box sx={{
                                p: 3,
                                pb: 1,
                                mb: 3,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
                                borderRadius: '16px',
                                border: '1px solid rgba(0, 67, 142, 0.1)',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #00438E, #4A87C8)',
                                    borderRadius: '16px 16px 0 0'
                                }
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2, textAlign: 'center' }}>
                                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#00438E', mb: 0.5, textAlign: 'center' }}>
                                            {responseDetail.data?.answers?.[0]?.survey_name || responseDetail.data?.survey_name || 'Unknown Assessment'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem', textAlign: 'center' }}>
                                            via {responseDetail.data?.answers?.[0]?.collector_name || responseDetail.data?.collector_name || 'Unknown Collector'} • {responseDetail.data?.answers?.length || 0} questions
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Questions List */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {responseDetail.data.answers.map((answer, index) => (
                                    <Card key={answer.id} sx={{
                                        borderRadius: "16px",
                                        boxShadow: "0 2px 8px rgba(0, 67, 142, 0.06)",
                                        border: "1px solid rgba(0, 67, 142, 0.08)",
                                        transition: "all 0.2s ease-in-out",
                                        overflow: 'hidden',
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            boxShadow: "0 4px 16px rgba(0, 67, 142, 0.1)",
                                            borderColor: "rgba(0, 67, 142, 0.15)"
                                        }
                                    }}>
                                        <CardContent sx={{ p: 3, pt: 1 }}>
                                            {/* Question Header */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, my: 1 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: '8px',
                                                                backgroundColor: '#00438E44',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                            }}>
                                                                <Typography sx={{ fontSize: '0.8rem', color: 'white', fontWeight: 600 }}>
                                                                    {index + 1}
                                                                </Typography>
                                                            </Box>

                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'text.primary',
                                                                    lineHeight: 1.5,
                                                                    fontSize: '0.9rem',
                                                                    fontWeight: 500,
                                                                    textOverflow: 'ellipsis',
                                                                    overflow: 'hidden',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {stripHtml(answer.question_text)}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={answer.question_type}
                                                            size="small"
                                                            sx={{
                                                                background: 'linear-gradient(135deg, #4A87C844,rgba(24, 73, 118, 0.27))',
                                                                color: 'white',
                                                                fontSize: '0.65rem',
                                                                height: '20px',
                                                                fontWeight: 600,
                                                                borderRadius: '10px'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Answer */}
                                            <Box sx={{
                                                p: 1.5,
                                                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                                borderRadius: '12px',
                                                border: '1px solid rgba(0, 67, 142, 0.08)',
                                                position: 'relative',
                                            }}>
                                                <Typography variant="body1" sx={{
                                                    color: 'text.primary',
                                                    lineHeight: 1.4,
                                                    fontSize: '0.8rem',
                                                    fontWeight: 400,
                                                    fontStyle: answer.text ? 'italic' : 'normal'
                                                }}>
                                                    {answer.text || answer.choice || (answer.value !== null ? answer.value : 'No answer provided')}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{
                            textAlign: "center",
                            py: 12,
                            px: 4,
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
                        }}>
                            <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(0, 67, 142, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                            }}>
                                <Typography variant="h2" color="#00438E">❓</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                                <FormattedMessage id="home.no_answers" defaultMessage="No answers found for this response." />
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                This response appears to be incomplete or contains no answers.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    p: 3,
                    borderTop: '1px solid rgba(0, 67, 142, 0.1)',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
                }}>
                    <Button
                        onClick={() => setResponseDetail({ data: null, loading: false })}
                        variant="contained"
                        size="large"
                        sx={{
                            background: 'linear-gradient(135deg, #00438E, #4A87C8)',
                            color: 'white',
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 16px rgba(0, 67, 142, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #003A75, #3A76B8)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(0, 67, 142, 0.4)'
                            }
                        }}
                    >
                        <FormattedMessage id="home.close" defaultMessage="Close" />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ResponsesModal;
